/**
 * The traveler's one-line "you" bubble after each Phase 1 step, and the
 * related wording helpers.
 */
import { addDays, dateRange } from './dates';
import { GROUP_OPTIONS } from './flowConfig';
import type { AlmaFlowAnswers, FlowGroup, FlowWalkingOption } from './types';

/** "Lisbon" / "Lisbon, then Porto" / "Lisbon, Sintra, then Porto". */
export function cityList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')}, then ${names[names.length - 1]}`;
}

/**
 * "Lisbon · Oct 14 – 20" for one city; "Porto Oct 14 – 16, then Lisbon Oct 18 – 21"
 * for several, so a day between two cities that isn't picked shows.
 */
export function whereWhenRecap(legs: { city: string; start_date: string; days: number }[]): string {
  const stays = legs.map((l) => ({ city: l.city, range: dateRange(l.start_date, addDays(l.start_date, l.days - 1)) }));
  if (stays.length === 1) return `${stays[0].city} · ${stays[0].range}`;
  return stays.map((s) => `${s.city} ${s.range}`).join(', then ');
}

/** "Just me" / "With a partner" / "With friends · 4 of us". */
export function whoRecap(group: FlowGroup, size: number): string {
  const label = GROUP_OPTIONS.find((g) => g.id === group)?.label ?? '';
  return group === 'friends' || group === 'family' ? `${label} · ${size} of us` : label;
}

/** First three, then "and N more": "Food & restaurants, Nature & hiking, Live fado and 2 more". */
export function picksRecap(labels: string[]): string {
  if (labels.length <= 3) {
    if (labels.length <= 1) return labels[0] ?? '';
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }
  return `${labels.slice(0, 3).join(', ')} and ${labels.length - 3} more`;
}

/** Recap label for a priority or branch pick: the traveler's own words for "Other". */
export function pickLabel(pick: { label: string; custom?: string }): string {
  return pick.custom ?? pick.label;
}

/** Walking option wording for this group, falling back to the default label. */
export function walkingLabel(option: FlowWalkingOption, group: FlowGroup | null): string {
  const byGroup = group === 'solo'
    ? option.label_solo
    : group === 'partner'
      ? option.label_partner
      : group === 'friends' || group === 'family'
        ? option.label_group
        : null;
  return byGroup?.trim() ? byGroup : option.label;
}

/** The priorities recap from saved answers. */
export function prioritiesRecap(priorities: AlmaFlowAnswers['priorities']): string {
  return picksRecap(priorities.map(pickLabel));
}
